import { Link, useNavigate } from "react-router";
import { DashboardCard } from "../molecules/DashboardCard";
import { DueDateCell } from "../molecules/DueDateCell";
import { type DocumentVersionSummary } from "~/api/types";
import { dateDiff } from "~/helpers/date";
import { useTranslation } from "react-i18next";
import NoResult from "../molecules/NoResult";
import SimpleDataTable, {
  type TableColumn,
} from "../molecules/SimpleDataTable";
import { CenterSpinner } from "../atoms/spinner";
import { Button } from "~/atoms/button";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { DocumentVersionStatus } from "~/config/types";

const UPCOMING_REVIEW_DAYS_THRESHOLD = 90;

const UpcomingWorkflowsContainer = () => {
  const { t } = useTranslation();

  const columns: TableColumn<DocumentVersionSummary>[] = [
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) => a.title.localeCompare(b.title),
      cell: (dv) => (
        <div className="min-w-0">
          <span className="block truncate" title={dv.title}>
            {dv.title}
          </span>
        </div>
      ),
      cellClassName: "w-full max-w-0",
      tableHeadClassName: "w-full",
    },
    {
      header: t(($) => $.commons.document.reviewFrom),
      defaultSort: true,
      compareFn: (a, b) => dateDiff(a.reviewFrom, b.reviewFrom),
      cell: (v) => <DueDateCell dueDate={v.reviewFrom} />,
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
    {
      header: t(($) => $.commons.document.reviewTo),
      compareFn: (a, b) => dateDiff(a.reviewTo, b.reviewTo),
      cell: (v) => <DueDateCell dueDate={v.reviewTo} />,
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
  ];
  const navigate = useNavigate();
  const { data: documentVersions, isLoading } = useDocumentVersions({
    statuses: [DocumentVersionStatus.draft],
    includeWorkflowData: true,
  });

  const upcomingWorkflows = documentVersions?.filter(
    (dv) =>
      !dv.isOngoing &&
      dv.status === DocumentVersionStatus.draft &&
      dv.reviewFrom &&
      dateDiff(dv.reviewFrom, new Date(), "day") <=
        UPCOMING_REVIEW_DAYS_THRESHOLD,
  );

  const title = t(($) => $.dashboard.upcoming.workflows);

  if (isLoading) {
    return (
      <DashboardCard title={title} className="h-80">
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (upcomingWorkflows?.length === 0) {
    return (
      <DashboardCard title={title}>
        <NoResult className="h-full" />
      </DashboardCard>
    );
  }

  const button = (
    <Button variant="link" asChild>
      <Link to="workflows?tab=upcoming">
        {t(($) => $.commons.buttons.seeAll)}
      </Link>
    </Button>
  );

  return (
    <DashboardCard title={title} button={button}>
      <SimpleDataTable
        data={upcomingWorkflows ?? []}
        columns={columns}
        onClickRow={(i) => navigate(`/documents/${i.document.id}?tab=workflow`)}
      />
    </DashboardCard>
  );
};

export default UpcomingWorkflowsContainer;
